<template>
    <div class="date-range">
        <!-- `left`: el campo está hacia la derecha de la barra; el panel (~440px) se abre
             hacia la izquierda en vez de salirse de la pantalla -->
        <v-menu v-model="open" :close-on-content-click="false" offset-y left nudge-bottom="4"
            transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="label" :label="title" readonly filled outlined hide-details
                    prepend-inner-icon="fas fa-calendar-alt" append-icon="fas fa-chevron-down"
                    class="date-range__field" v-bind="attrs" v-on="on">
                </v-text-field>
            </template>
            <div class="date-range__panel">
                <ul class="date-range__presets" role="listbox" :aria-label="title">
                    <li v-for="preset in presets" :key="preset.key" role="option"
                        :aria-selected="String(preset.key === activeKey)">
                        <button type="button" class="date-range__preset"
                            :class="{ 'is-active': preset.key === activeKey }" @click="choose(preset)">
                            {{ preset.label }}
                        </button>
                    </li>
                    <li role="option" :aria-selected="String(activeKey === 'all')">
                        <button type="button" class="date-range__preset"
                            :class="{ 'is-active': activeKey === 'all' }" @click="choose({ start: '', end: '' })">
                            {{ allLabel }}
                        </button>
                    </li>
                </ul>
                <v-date-picker v-model="picked" range no-title locale="es" color="primary" :max="maxDate"
                    @input="onPick">
                </v-date-picker>
            </div>
        </v-menu>
    </div>
</template>
<script>
import { DateTime } from 'luxon';
import { ALL_DATES_LABEL, describeRange, matchPreset, presetList, sortRange } from '@/utils/dateRange';

/**
 * Campo de rango de fechas para las listas: un solo campo que dice "Hoy",
 * "Ayer", "Este mes" o "01/10/2026 – 15/10/2026", en vez de dos campos de fecha.
 *
 * Al abrirlo: atajos a la izquierda (Hoy, Ayer, Últimos 7 días, Este mes, Mes
 * pasado, Todas las fechas; más 'Esta semana' si la vista lo pide con `extra-presets`) y un calendario de rango. Elegir un atajo, o
 * tocar la segunda fecha del calendario, aplica y cierra.
 *
 *   <DateRangeField :start.sync="datesRange.start" :end.sync="datesRange.end" />
 *
 * `start` y `end` son texto `yyyy-MM-dd`; vacíos significa "todas las fechas".
 * Emite los dos a la vez, así que la vista hace una sola consulta.
 *
 * Aparte de `SingleCalendar` (un solo día, compartido por ~25 vistas), que no
 * se toca. Vuetify 3: `v-date-picker range` pasa a `multiple="range"` y el
 * slot activador del menú a `{ props }`.
 */
export default {
    name: 'DateRangeField',
    props: {
        start: { type: String, default: '' },
        end: { type: String, default: '' },
        title: { type: String, default: 'Fecha' },
        // Las ventas no se registran en el futuro; sin esto el calendario deja elegirlo.
        maxToday: { type: Boolean, default: true },
        // Atajos opcionales que esta vista sí quiere (hoy solo 'week' = Esta semana, lunes a domingo). Sin esto no salen.
        extraPresets: { type: Array, default: () => [] }
    },
    data() {
        return { open: false, picked: [] };
    },
    computed: {
        allLabel() {
            return ALL_DATES_LABEL;
        },
        presets() {
            return presetList(undefined, this.extraPresets);
        },
        label() {
            return describeRange(this.start, this.end, undefined, this.extraPresets);
        },
        activeKey() {
            return matchPreset(this.start, this.end, undefined, this.extraPresets);
        },
        maxDate() {
            return this.maxToday ? DateTime.now().toFormat('yyyy-MM-dd') : undefined;
        }
    },
    watch: {
        // al abrir, el calendario arranca en el rango aplicado
        open(isOpen) {
            if (isOpen) this.picked = [this.start, this.end].filter(Boolean);
        }
    },
    methods: {
        apply({ start, end }) {
            this.$emit('update:start', start);
            this.$emit('update:end', end);
            this.open = false;
        },
        choose(range) {
            this.apply(range);
        },
        // El calendario da un arreglo: con una fecha espera la segunda, con dos aplica.
        onPick(dates) {
            if (!Array.isArray(dates) || dates.length < 2) return;
            this.apply(sortRange(dates[0], dates[1]));
        }
    }
};
</script>
<style scoped>
.date-range {
    flex: 1 1 170px;
    max-width: 220px;
    min-width: 0;
}
@media (max-width: 599px) {
    .date-range {
        max-width: none;
    }
}
.date-range__panel {
    display: flex;
    background: var(--app-surface);
}
.date-range__presets {
    margin: 0;
    padding: 8px;
    list-style: none;
    border-right: 1px solid var(--app-line);
}
.date-range__preset {
    display: block;
    width: 100%;
    min-height: 36px;
    padding: 0 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--app-text);
    font: inherit;
    font-size: 14px;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
}
.date-range__preset:hover {
    background: var(--app-hover);
}
.date-range__preset:focus-visible {
    outline: 2px solid var(--app-primary);
    outline-offset: -2px;
}
.date-range__preset.is-active {
    background: var(--app-primary-soft);
    color: var(--app-primary);
    font-weight: 600;
}
/* celular: los atajos pasan arriba, en filas que se acomodan */
@media (max-width: 599px) {
    .date-range__panel {
        flex-direction: column;
    }
    .date-range__presets {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        border-right: 0;
        border-bottom: 1px solid var(--app-line);
    }
    .date-range__presets li {
        flex: 0 0 auto;
    }
    .date-range__preset {
        width: auto;
        min-height: 40px;
        border: 1px solid var(--app-line-strong);
    }
}
</style>
